import type {
  CatalogBillingMethod,
  CreateProductPaymentMethodPayload,
  PaymentMethodStatus,
  ProductPaymentMethod,
  UpdateProductPaymentMethodPayload,
} from '@/services';
import { z } from 'zod';
import {
  BILLING_METHOD_REQUIRING_BANK,
  NO_GATEWAY_USE,
} from '../utils/payment-settings-labels.util';

/** Campos do formulário de uma forma do catálogo. */
export const paymentMethodFieldsSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(
      /^[A-Za-z0-9_-]{1,20}$/,
      'Use letras, números, - ou _ (até 20 caracteres).'
    ),
  description: z
    .string()
    .trim()
    .min(1, 'Informe a descrição.')
    .max(100, 'Use até 100 caracteres.'),
  billingMethod: z.enum([
    'PIX',
    'CARTAO_CREDITO',
    'BOLETO_PIX',
    'TODOS_MEIOS',
    'FICHA_COMPENSACAO',
    'DEBITO_CONTA',
    NO_GATEWAY_USE,
  ]),
  bankNumber: z
    .string()
    .trim()
    .refine(
      value => value === '' || /^\d{1,4}$/.test(value),
      'Informe o código do banco (até 4 dígitos).'
    ),
  status: z.enum(['ACTIVE', 'INACTIVE']),
});

export type PaymentMethodFormValues = z.infer<typeof paymentMethodFieldsSchema>;

export const BANK_REQUIRED_MESSAGE = 'Débito em conta exige o código do banco.';

/** A regra que cruza campos: débito em conta exige o banco. */
export function paymentMethodBankError(
  values: PaymentMethodFormValues
): string | undefined {
  return values.billingMethod === BILLING_METHOD_REQUIRING_BANK &&
    !values.bankNumber.trim()
    ? BANK_REQUIRED_MESSAGE
    : undefined;
}

export const EMPTY_PAYMENT_METHOD_FORM: PaymentMethodFormValues = {
  code: '',
  description: '',
  billingMethod: NO_GATEWAY_USE,
  bankNumber: '',
  status: 'ACTIVE',
};

export function toPaymentMethodFormValues(
  method: ProductPaymentMethod
): PaymentMethodFormValues {
  return {
    code: method.code,
    description: method.description,
    billingMethod: method.billingMethod ?? NO_GATEWAY_USE,
    bankNumber:
      method.bankNumber !== undefined ? String(method.bankNumber) : '',
    status: method.status,
  };
}

type MethodFields = {
  description: string;
  billingMethod?: CatalogBillingMethod;
  bankNumber?: number;
  status: PaymentMethodStatus;
};

function toMethodFields(values: PaymentMethodFormValues): MethodFields {
  return {
    description: values.description.trim(),
    billingMethod:
      values.billingMethod === NO_GATEWAY_USE
        ? undefined
        : values.billingMethod,
    bankNumber: values.bankNumber.trim()
      ? Number(values.bankNumber)
      : undefined,
    status: values.status,
  };
}

export function toCreatePaymentMethodPayload(
  values: PaymentMethodFormValues
): CreateProductPaymentMethodPayload {
  return { ...toMethodFields(values), code: values.code.trim() };
}

export function toUpdatePaymentMethodPayload(
  values: PaymentMethodFormValues,
  version: number
): UpdateProductPaymentMethodPayload {
  return { ...toMethodFields(values), version };
}
